import * as React from 'react';

import { useSpring, animated, config } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { BackButton } from '../EditItineraryModal/BackButton';
import styles from './ItineraryDetails.module.css';
import { Itinerary } from 'models/itinerary';
import { DateSelector } from './DateSelector';
import { ItemCard } from './ItemCard/ItemCard';
import { useMapDisplayContext } from 'components/Map/MapDisplayContext';
import { AnimatedClickableDiv } from 'components/Map/AnimatedClickableDiv';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useIsSmallScreen } from 'components/Map/useIsSmallScreen';
import { AlertSnackbar } from '../AlertSnackbar';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { useSelector } from 'react-redux';

export const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
    <path
      d="M6.29548 15.0462L1.10352 16.5L2.55727 11.3081L12.0643 1.84714C12.1718 1.73725 12.3002 1.64993 12.4417 1.59032C12.5834 1.53071 12.7356 1.5 12.8893 1.5C13.043 1.5 13.1951 1.53071 13.3368 1.59032C13.4784 1.64993 13.6068 1.73725 13.7142 1.84714L15.7564 3.90085C15.8645 4.00811 15.9503 4.13572 16.009 4.27631C16.0676 4.41691 16.0977 4.56772 16.0977 4.72003C16.0977 4.87234 16.0676 5.02315 16.009 5.16374C15.9503 5.30434 15.8645 5.43195 15.7564 5.53921L6.29548 15.0462Z"
      stroke="#393D3F"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

export const ShareIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
    <path
      d="M3.32031 9.75C4.56295 9.75 5.57031 8.74264 5.57031 7.5C5.57031 6.25736 4.56295 5.25 3.32031 5.25C2.07767 5.25 1.07031 6.25736 1.07031 7.5C1.07031 8.74264 2.07767 9.75 3.32031 9.75Z"
      stroke="#3AA0FF"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M11.8203 14C13.0629 14 14.0703 12.9926 14.0703 11.75C14.0703 10.5074 13.0629 9.5 11.8203 9.5C10.5777 9.5 9.57031 10.5074 9.57031 11.75C9.57031 12.9926 10.5777 14 11.8203 14Z"
      stroke="#3AA0FF"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M11.8203 5.5C13.0629 5.5 14.0703 4.49264 14.0703 3.25C14.0703 2.00736 13.0629 1 11.8203 1C10.5777 1 9.57031 2.00736 9.57031 3.25C9.57031 4.49264 10.5777 5.5 11.8203 5.5Z"
      stroke="#3AA0FF"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M5.33008 6.5L9.81008 4.25"
      stroke="#3AA0FF"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M5.33008 8.5L9.81008 10.75"
      stroke="#3AA0FF"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);
interface ItineraryDetailsProps {
  itinerary: Itinerary;
  onBackClick: () => void;
  alwaysMaximized?: boolean;
  onExpandClick?: () => void;
}

export const ItineraryDetails: React.FC<ItineraryDetailsProps> = ({
  itinerary,
  onBackClick,
  alwaysMaximized = false,
  onExpandClick,
}) => {
  const isSmallScreen = useIsSmallScreen();
  const { t } = useTranslation();
  const [snackbarText, setSnackbarText] = React.useState<string>('');
  const [position, setPosition] = React.useState<'MINIMIZED' | 'MAXIMIZED'>(
    isSmallScreen && !alwaysMaximized ? 'MINIMIZED' : 'MAXIMIZED'
  );
  const modalRef = React.useRef<HTMLDivElement>(null);
  const {
    setSelectedItineraryDate,
    selectedItineraryDate,
    replaceUrlParams,
    setNewItinerary,
    setPinInfoDisplayState,
  } = useMapDisplayContext();
  const digitalMap = useSelector(selectDigitalMap);
  const isRecommended = digitalMap?.recommended_itineraries?.some(
    (recommendedItinerary) => recommendedItinerary.id === itinerary.id
  );

  const [springs] = useSpring(() => ({
    from: { y: 400 },
    to: { y: 0 },
    config: {
      ...config.stiff,
      clamp: true,
    },
    immediate: !isSmallScreen || alwaysMaximized,
  }));

  React.useEffect(() => {
    if (!selectedItineraryDate && (itinerary?.date_itineraries?.length ?? 0) > 0) {
      setSelectedItineraryDate(itinerary.date_itineraries[0].date);
    }
  }, [selectedItineraryDate, itinerary, setSelectedItineraryDate]);

  const bind = useDrag(
    ({ swipe: [, swipeY] }) => {
      if (alwaysMaximized) return;

      if (swipeY < 0 && position === 'MINIMIZED') {
        setPosition('MAXIMIZED');
      } else if (swipeY > 0 && position === 'MAXIMIZED' && isSmallScreen) {
        if (modalRef.current) {
          modalRef.current.scrollTop = 0;
        }
        setPosition('MINIMIZED');
      }
    },
    {
      swipe: {
        distance: [0, 10],
        velocity: [0, 0.1],
        duration: 250,
      },
    }
  );

  const modalStyle: React.CSSProperties = {
    height:
      position === 'MAXIMIZED'
        ? 'calc(100dvh - var(--header-height) - var(--bottom-nav-height))'
        : '280px',
    overflowY: position === 'MAXIMIZED' ? 'auto' : 'hidden',
    borderRadius: position === 'MAXIMIZED' ? '0' : '16px 16px 0 0',
  };

  const handleShare = async () => {
    const shareData = {
      title: itinerary.title,
      text: `Check out this itinerary: ${itinerary.title}`,
      url: window.location.href,
    };

    try {
      if (isSmallScreen && navigator.share) {
        await navigator.share(shareData);
      } else {
        // Use copy logic for desktop or when native share is not available
        await navigator.clipboard.writeText(window.location.href);
        setSnackbarText(t('Itinerary link copied to clipboard!'));
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  return (
    <>
      {snackbarText && <AlertSnackbar message={snackbarText} onClose={() => setSnackbarText('')} />}
      <div className={styles.container}>
        {position === 'MINIMIZED' && (
          <div className={styles.buttonsContainer}>
            <div className={styles.backButtonContainer}>
              <BackButton onClick={onBackClick} />
            </div>

            <div className={clsx(styles.actionButtonsContainer, styles.minimized)}>
              <AnimatedClickableDiv
                className={styles.icon}
                clickedClassName={styles.iconClicked}
                onClick={handleShare}
              >
                <ShareIcon />
              </AnimatedClickableDiv>
              {itinerary.owned && (
                <AnimatedClickableDiv
                  className={styles.icon}
                  clickedClassName={styles.iconClicked}
                  onClick={async () => {
                    await replaceUrlParams({ itin: itinerary.id, pg: 'ITINERARIES.EDIT' });
                    setNewItinerary(itinerary);
                    setSelectedItineraryDate(null);
                  }}
                >
                  <EditIcon />
                </AnimatedClickableDiv>
              )}
            </div>
          </div>
        )}
        <div className={styles.modalWrapper}>
          <animated.div
            {...(bind() as any)}
            ref={modalRef}
            className={styles.modal}
            style={{
              ...modalStyle,
              ...springs,
            }}
          >
            {position === 'MINIMIZED' && (
              <div
                className={styles.handleContainer}
                onClick={() => {
                  setPosition('MAXIMIZED');
                }}
              >
                <div className={styles.handle} />
              </div>
            )}
            {onExpandClick && (
              <img
                onClick={onExpandClick}
                src="/static/images/map/enlarge.svg"
                className={styles.enlargeIcon}
              />
            )}
            <div className={clsx(styles.header, position === 'MAXIMIZED' && styles.maximized)}>
              {position === 'MAXIMIZED' && (
                <div className={styles.maximizedBackButtonContainer}>
                  <BackButton onClick={onBackClick} />
                </div>
              )}
              <h2>{itinerary.title}</h2>
              {position === 'MAXIMIZED' && (
                <div className={styles.actionButtonsContainer}>
                  <AnimatedClickableDiv
                    className={styles.icon}
                    clickedClassName={styles.iconClicked}
                    onClick={handleShare}
                  >
                    <ShareIcon />
                  </AnimatedClickableDiv>
                  {itinerary.owned && (
                    <AnimatedClickableDiv
                      className={styles.icon}
                      clickedClassName={styles.iconClicked}
                      onClick={async () => {
                        await replaceUrlParams({ itin: itinerary.id, pg: 'ITINERARIES.EDIT' });
                        setNewItinerary(itinerary);
                        setSelectedItineraryDate(null);
                      }}
                    >
                      <EditIcon />
                    </AnimatedClickableDiv>
                  )}
                </div>
              )}
            </div>

            <div className={styles.content}>
              <DateSelector
                isRecommended={isRecommended}
                dates={itinerary.date_itineraries.map((date) => date.date)}
                onDateSelect={setSelectedItineraryDate}
                activeDate={selectedItineraryDate ?? ''}
              />

              <div className={styles.itemCardsContainer}>
                {itinerary.date_itineraries
                  .find((date) => date.date === selectedItineraryDate)
                  ?.items.map((item) => (
                    <ItemCard
                      key={item.pin_key}
                      item={item}
                      onClick={() => {
                        setPinInfoDisplayState({ pinKey: item.pin_key, displayState: 'DETAILS' });
                      }}
                    />
                  ))}
              </div>
            </div>
          </animated.div>
        </div>
      </div>
    </>
  );
};
